import React, { useMemo } from "react";
import { GENERATOR_CODES } from "@csk/core";
import {
  DemoResponsiveGrid,
  DemoSparkBarChart,
  DemoWorkbenchCard,
  DemoWorkbenchItemList,
  DemoWorkbenchMetricCard,
  DemoWorkbenchPill,
  DemoWorkbenchStack,
  type DemoPillVariant,
} from "@csk/demo-ui";
import {
  POLICY_SCHEDULE_EVENTS,
  buildPolicyTrajectory,
  type PolicyScheduleRegime,
} from "./policy-schedule-panel/policyScheduleModel.js";

const REGIME_VARIANT: Record<PolicyScheduleRegime, DemoPillVariant> = {
  R1: "success",
  R2: "warning",
  R3: "danger",
};

const REGIME_BAR_COLOR: Record<PolicyScheduleRegime, string> = {
  R1: "#22c55e",
  R2: "#f59e0b",
  R3: "#ef4444",
};

const PolicySchedulePanel = (): React.JSX.Element => {
  const events = POLICY_SCHEDULE_EVENTS;
  const { maxKappa, points } = useMemo(
    () => buildPolicyTrajectory(events),
    [events],
  );

  return (
    <DemoWorkbenchStack>
      <DemoWorkbenchCard title="Policy Schedule Simulator (§17)">
        Policy changes are replayed as a bounded footprint trajectory.
      </DemoWorkbenchCard>
      <DemoWorkbenchCard compact title="Scheduled events">
        <DemoWorkbenchItemList
          items={events.map((event) => ({
            id: `${event.time}-${event.label}`,
            label: `${event.label} · t=${event.time}`,
            detail: `Δ=[${event.delta.join(",")}]`,
          }))}
        />
      </DemoWorkbenchCard>

      <DemoWorkbenchCard compact title="κ trajectory">
        <DemoSparkBarChart
          ariaLabel="Kappa trajectory by policy time"
          bars={points.map((point) => ({
            id: point.time,
            label: `t=${point.time}`,
            value: point.kappa,
            color: REGIME_BAR_COLOR[point.regime],
            title: `t=${point.time}; κ=${point.kappa}`,
            valueLabel: (
              <DemoWorkbenchPill variant={REGIME_VARIANT[point.regime]} mono>
                κ={point.kappa}
              </DemoWorkbenchPill>
            ),
          }))}
          height={96}
          max={maxKappa}
        />
      </DemoWorkbenchCard>

      <DemoResponsiveGrid minColumnWidth={120} gap={8}>
        {GENERATOR_CODES.map((axis, ai) => (
          <DemoWorkbenchMetricCard
            key={axis}
            label={axis}
            value={
              <span className="flex justify-center gap-1">
                {points.map((point) => {
                  const value = point.footprint[ai as 0 | 1 | 2 | 3];
                  return (
                    <DemoWorkbenchPill
                      key={`${point.time}-${axis}`}
                      variant={value > 0 ? "info" : "default"}
                      mono
                    >
                      {value}
                    </DemoWorkbenchPill>
                  );
                })}
              </span>
            }
            sub="footprint timeline"
          />
        ))}
      </DemoResponsiveGrid>
    </DemoWorkbenchStack>
  );
};

export default PolicySchedulePanel;
